import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Chart } from 'chart.js';
import 'chart.js/auto';
import './home.css';
import { DashboardCard } from '@/components/DashboardCard/DashboardCard';
import { Bar } from 'react-chartjs-2';
import {
  dashboardKeys,
  fetchDashboardHome,
} from '@/services/dashboardService';
import type { IDashboardHome } from '@/types/dashboard';
import { useAnalytics, useLogger, useMetrics } from '@/observability';

export const Route = createFileRoute('/_authenticated/home/')({
  component: Home,
});

const CHART_COLORS = {
  entrada: '#43426a',
  saida: '#b0b0c3',
} as const;

const CHART_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
  },
  datasets: {
    bar: {
      barPercentage: 0.9,
      categoryPercentage: 0.6,
      borderRadius: 4,
    },
  },
  scales: {
    y: {
      grid: {
        display: true,
      },
      ticks: {
        display: false,
      },
      border: {
        display: false,
      },
    },
    x: {
      grid: {
        display: false,
      },
      ticks: {
        padding: 0,
      },
    },
  },
};

interface ITrendInfo {
  value: string;
  arrow: string;
  className: string;
  srText: string;
}

function formatCompact(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
}

function formatOcupacaoTrend(variacao: number): ITrendInfo {
  const rounded = Math.round(variacao * 10) / 10;

  if (rounded > 0) {
    return {
      value: `+${rounded.toFixed(1)}%`,
      arrow: '↗',
      className: 'trend--up',
      srText: 'Tendência de alta em relação ao período anterior.',
    };
  }

  if (rounded < 0) {
    return {
      value: `${rounded.toFixed(1)}%`,
      arrow: '↘',
      className: 'trend--urgent',
      srText: 'Tendência de baixa em relação ao período anterior.',
    };
  }

  return {
    value: '0%',
    arrow: '→',
    className: 'trend--neutral',
    srText: 'Tendência estável em relação ao período anterior.',
  };
}

function buildChartData(data: IDashboardHome | undefined) {
  const buckets = data?.movimentacao_24h ?? [];
  return {
    labels: buckets.map((b) => b.label),
    datasets: [
      {
        label: 'Entrada',
        data: buckets.map((b) => b.entrada),
        backgroundColor: CHART_COLORS.entrada,
      },
      {
        label: 'Saída',
        data: buckets.map((b) => b.saida),
        backgroundColor: CHART_COLORS.saida,
      },
    ],
  };
}

function isMovimentacaoEmpty(data: IDashboardHome | undefined): boolean {
  const buckets = data?.movimentacao_24h ?? [];
  if (buckets.length === 0) return true;
  return buckets.every((b) => b.entrada === 0 && b.saida === 0);
}

function Home() {
  const chartRef = useRef<Chart<'bar'>>(null);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const logger = useLogger();
  const metrics = useMetrics();
  const analytics = useAnalytics();

  const { data, isLoading, isError } = useQuery({
    queryKey: dashboardKeys.home,
    queryFn: fetchDashboardHome,
  });

  useEffect(() => {
    logger.info('Home mounted');
    analytics.track('Home Viewed');
  }, [logger, analytics]);

  useEffect(() => {
    if (!data) return;
    metrics.gauge('home.dashboard.loaded', 1);
  }, [data, metrics]);

  useEffect(() => {
    const container = chartContainerRef.current;
    if (!container) return;

    const resizeChart = () => {
      chartRef.current?.resize();
    };

    const observer = new ResizeObserver(resizeChart);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  const hasData = Boolean(data);
  const chartData = buildChartData(data);
  const chartIsEmpty = isMovimentacaoEmpty(data);
  const ocupacaoTrend = formatOcupacaoTrend(data?.ocupacao.variacao_percentual ?? 0);
  const ocupacaoPct = Math.round(data?.ocupacao.percentual ?? 0);
  const expedicaoUrgente = Boolean(data?.expedicoes.urgente);

  return (
    <div
      className="home"
      aria-busy={isLoading}
    >
      <div
        className="sr-only"
        aria-live="polite"
      >
        {isLoading ? 'Carregando dados do dashboard.' : ''}
        {isError ? 'Falha ao carregar os dados do dashboard.' : ''}
      </div>

      {isError ? (
        <p role="alert">Não foi possível carregar o dashboard.</p>
      ) : null}

      <div className="header">
        <DashboardCard>
          <DashboardCard.Icon>
            <span
              className="dashboard-card-letter"
              aria-hidden="true"
            >
              O
            </span>
          </DashboardCard.Icon>

          <DashboardCard.Trend>
            {isLoading ? (
              <span
                className="skeleton skeleton--trend"
                aria-hidden="true"
              />
            ) : (
              <span className={`trend ${ocupacaoTrend.className}`}>
                <span aria-hidden="true">
                  {ocupacaoTrend.value} {ocupacaoTrend.arrow}
                </span>
                <span className="sr-only">{ocupacaoTrend.srText}</span>
              </span>
            )}
          </DashboardCard.Trend>

          <DashboardCard.Title>
            Ocupação do Armazém
          </DashboardCard.Title>

          <DashboardCard.Value>
            {isLoading ? (
              <span
                className="skeleton skeleton--value"
                aria-hidden="true"
              />
            ) : (
              <>
                {hasData ? ocupacaoPct : '—'}
                <small>%</small>
              </>
            )}
          </DashboardCard.Value>

          <DashboardCard.Footer>
            {isLoading ? (
              <span
                className="skeleton skeleton--footer"
                aria-hidden="true"
              />
            ) : hasData ? (
              `${data?.ocupacao.enderecos_ocupados} de ${data?.ocupacao.enderecos_totais} endereços ocupados`
            ) : (
              'Capacidade utilizada no momento'
            )}
          </DashboardCard.Footer>
        </DashboardCard>

        <DashboardCard>
          <DashboardCard.Icon>
            <span
              className="dashboard-card-letter"
              aria-hidden="true"
            >
              R
            </span>
          </DashboardCard.Icon>

          <DashboardCard.Trend>
            {isLoading ? (
              <span
                className="skeleton skeleton--trend"
                aria-hidden="true"
              />
            ) : (
              <span className="trend trend--neutral">
                {hasData
                  ? `Total: ${formatCompact(data?.recebimentos.total_caixas_hoje ?? 0)}`
                  : '—'}
              </span>
            )}
          </DashboardCard.Trend>

          <DashboardCard.Title>
            Recebimentos Hoje
          </DashboardCard.Title>

          <DashboardCard.Value>
            {isLoading ? (
              <span
                className="skeleton skeleton--value"
                aria-hidden="true"
              />
            ) : hasData ? (
              data?.recebimentos.quantidade_hoje ?? 0
            ) : (
              '—'
            )}
          </DashboardCard.Value>

          <DashboardCard.Footer>
            {isLoading ? (
              <span
                className="skeleton skeleton--footer"
                aria-hidden="true"
              />
            ) : hasData ? (
              `${data?.recebimentos.em_espera ?? 0} carretas em espera`
            ) : (
              '—'
            )}
          </DashboardCard.Footer>
        </DashboardCard>

        <DashboardCard>
          <DashboardCard.Icon>
            <span
              className="dashboard-card-letter"
              aria-hidden="true"
            >
              E
            </span>
          </DashboardCard.Icon>

          <DashboardCard.Trend>
            {isLoading ? (
              <span
                className="skeleton skeleton--trend"
                aria-hidden="true"
              />
            ) : (
              <span className={`trend ${expedicaoUrgente ? 'trend--urgent' : 'trend--neutral'}`}>
                {hasData ? (expedicaoUrgente ? 'Urgente!' : 'Em dia') : '—'}
              </span>
            )}
          </DashboardCard.Trend>

          <DashboardCard.Title>
            Expedições Pendentes
          </DashboardCard.Title>

          <DashboardCard.Value>
            {isLoading ? (
              <span
                className="skeleton skeleton--value"
                aria-hidden="true"
              />
            ) : hasData ? (
              String(data?.expedicoes.pendentes ?? 0).padStart(2, '0')
            ) : (
              '—'
            )}
          </DashboardCard.Value>

          <DashboardCard.Footer>
            {isLoading ? (
              <span
                className="skeleton skeleton--footer"
                aria-hidden="true"
              />
            ) : hasData ? (
              `Check-out finalizado: ${data?.expedicoes.finalizados_hoje ?? 0}`
            ) : (
              '—'
            )}
          </DashboardCard.Footer>
        </DashboardCard>
      </div>

      <div className="content">
        <div className="graph-bar">
          <div className="graph-bar-header">
            <div className="graph-bar-header-title">
              <h2>Movimentação de Cargas</h2>
              <span>Entrada vs Saída (Últimas 24h)</span>
            </div>

            <div className="graph-bar-header-legend">
              <span className="graph-bar-legend-item">
                <span
                  className="graph-bar-legend-dot"
                  style={{ backgroundColor: CHART_COLORS.entrada }}
                />
                Entrada
              </span>
              <span className="graph-bar-legend-item">
                <span
                  className="graph-bar-legend-dot"
                  style={{ backgroundColor: CHART_COLORS.saida }}
                />
                Saída
              </span>
            </div>
          </div>

          <div
            className="graph-bar-content"
            ref={chartContainerRef}
            aria-busy={isLoading}
          >
            {isLoading ? (
              <div
                className="chart-skeleton"
                aria-hidden="true"
              />
            ) : isError ? (
              <p className="chart-message">
                Gráfico indisponível — não foi possível carregar os dados de movimentação.
              </p>
            ) : chartIsEmpty ? (
              <p className="chart-message">
                Nenhuma movimentação registrada nas últimas 24 horas.
              </p>
            ) : (
              <Bar
                ref={chartRef}
                options={CHART_OPTIONS}
                data={chartData}
                role="img"
                aria-label={
                  'Gráfico de barras: movimentação de cargas, ' +
                  'entrada versus saída nas últimas 24 horas'
                }
              />
            )}
          </div>

          {!isLoading && !isError && !chartIsEmpty ? (
            <div className="sr-only">
              <table>
                <caption>Movimentação de cargas nas últimas 24 horas (entrada e saída)</caption>
                <thead>
                  <tr>
                    <th scope="col">Horário</th>
                    <th scope="col">Entrada</th>
                    <th scope="col">Saída</th>
                  </tr>
                </thead>
                <tbody>
                  {chartData.labels.map((label, index) => (
                    <tr key={label}>
                      <th scope="row">{label}</th>
                      <td>{chartData.datasets[0].data[index]}</td>
                      <td>{chartData.datasets[1].data[index]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
